import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllSilos } from '../../services/Silo/siloService';
import CreateSiloModal from './CreateSiloModal';
import EditSiloModal from './EditSiloModal';
import SiloFilter from '../Filters/SiloFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Silo = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setSilos] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateSiloModal, setShowCreateSiloModal] = useState(false);
  const [showEditSiloModal, setShowEditSiloModal] = useState(false);
  const [selectedSilo, setSelectedSilo] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('siloView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('siloCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('siloUpdate'), [hasPermission, permissions]);

  const fetchSilos = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllSilos(filters, currentPage - 1, currentPageSize);
      setSilos(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchSilos();
    }
  }, [page, pageSize, fetchSilos, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateSiloModal = () => {
    if (!checkPermission('siloCreate')) {
      return;
    }
    setShowCreateSiloModal(true);
  };
  const handleCloseCreateSiloModal = () => setShowCreateSiloModal(false);

  const handleOpenEditSiloModal = (silo) => {
    if (!checkPermission('siloUpdate')) {
      return;
    }
    setSelectedSilo(silo);
    setShowEditSiloModal(true);
  };
  
  const handleCloseEditSiloModal = () => {
    setSelectedSilo(null);
    setShowEditSiloModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchSilos(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchSilos({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Silos</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Silos</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateSiloModal}>
              Cadastrar Silo
            </Button>
          )}
        </Col>
      </Row>
      {canView && <SiloFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Capacidade (kg)</th>
              <th>Safra</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((silo) => (
              <tr key={silo.id}>
                <td>{silo.name}</td>
                <td>{silo.capacity.toFixed(3)}
                </td>
                <td>{silo.harvest !== null ? silo.harvest.description : ''}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditSiloModal(silo)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateSiloModal
        show={showCreateSiloModal}
        handleClose={handleCloseCreateSiloModal}
        refreshSilos={fetchSilos}
      />

      <EditSiloModal
        show={showEditSiloModal}
        handleClose={handleCloseEditSiloModal}
        silo={selectedSilo}
        refreshSilos={fetchSilos}
      />
    </Container>
  );
};

export default Silo;